import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Plus, ListTodo, User } from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { parseEffort } from '../lib/helpers';
import { useAuth } from '../lib/AuthContext';
import { Glass } from '../components/glass/Glass';
import { Pill } from '../components/primitives/Badge';
import { TaskRow } from '../components/cards/TaskRow';
import { ViewHero } from '../components/cards/ViewHero';

// Inline task creation input
const InlineTaskInput = ({ priority, roleSlug, goalId, onCreated }) => {
  const [value, setValue] = useState('');
  const [creating, setCreating] = useState(false);

  const create = async () => {
    const title = value.trim();
    if (!title) return;
    setCreating(true);
    const effortMatch = title.match(/\[(\d+[hm](?:\s*\d+[hm])?)\]$/i);
    const effort = effortMatch ? effortMatch[1] : null;
    const cleanTitle = effort ? title.replace(/\s*\[.*\]$/, '') : title;

    const durationMatch = cleanTitle.match(/\[(\d+)m\]$/i) || cleanTitle.match(/\[(\d+)h\]$/i);
    let duration_minutes = 30;
    if (effort) {
      const m = effort.match(/(\d+)h/);
      const m2 = effort.match(/(\d+)m/);
      duration_minutes = (m ? parseInt(m[1]) * 60 : 0) + (m2 ? parseInt(m2[1]) : 0);
    }

    await supabase.from('tasks').insert({
      title: cleanTitle,
      priority,
      effort: effort || null,
      duration_minutes: duration_minutes || 30,
      role_id: roleSlug || null,
      goal_id: goalId || null,
      done: false,
      status: 'todo',
      auto_schedule: true,
      sort_order: 0,
    });
    setValue('');
    setCreating(false);
    onCreated();
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
      borderRadius: 10, background: 'rgba(255,255,255,0.5)',
      border: '1px dashed rgba(0,0,0,0.10)', marginBottom: 8,
    }}>
      <Plus size={14} color={T.textLight} style={{ flexShrink: 0 }} />
      <input
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') create(); }}
        placeholder="Add a task... [30m]"
        disabled={creating}
        style={{
          flex: 1, border: 'none', background: 'transparent',
          fontSize: 13, color: T.text, outline: 'none',
          fontFamily: 'inherit', opacity: creating ? 0.5 : 1,
        }}
      />
      {value.trim() && (
        <button onClick={create} disabled={creating} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: T.amber, color: '#fff', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
        }}>Add</button>
      )}
    </div>
  );
};

export const TasksView = ({ tasks, goals, onUpdate, triggerScheduler }) => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [mineOnly, setMineOnly] = useState(false);
  const open = tasks.filter(t => !t.done && t.status !== 'done');
  const all = mineOnly ? open.filter(t => t.assignee_id === user?.id) : open;
  const filtered = filter === 'all' ? all : all.filter(t => t.roles?.slug === filter || t.goals?.roles?.slug === filter);
  const p1 = filtered.filter(t => t.priority === 1);
  const p2 = filtered.filter(t => t.priority === 2);
  const doneAll = tasks.filter(t => t.done || t.status === 'done');
  const done = mineOnly ? doneAll.filter(t => t.assignee_id === user?.id) : doneAll;
  const mineCount = open.filter(t => t.assignee_id === user?.id).length;

  const handleUpdate = () => {
    onUpdate();
    if (triggerScheduler) triggerScheduler();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <ViewHero
        icon={ListTodo}
        gradient="linear-gradient(135deg, #8FCBE8 0%, #4A9ECC 100%)"
        title="Tasks"
        stats={[
          { label: `${p1.length} urgent`, color: T.red, bg: 'rgba(224,88,88,0.10)', border: 'rgba(224,88,88,0.18)' },
          { label: `${p2.length} this week`, color: T.blue, bg: 'rgba(74,158,204,0.10)', border: 'rgba(74,158,204,0.18)' },
          { label: `${done.length} done`, color: T.green, bg: 'rgba(90,158,120,0.10)', border: 'rgba(90,158,120,0.18)' },
        ]}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill
          active={mineOnly}
          color={T.blue} bg={T.blueLight} border="rgba(74,158,204,0.25)"
          onClick={() => setMineOnly(m => !m)}
        >
          <User size={12} style={{ marginRight: 5, verticalAlign: '-2px' }} />
          Mine
          {mineCount > 0 && <span style={{ marginLeft: 5, opacity: 0.65, fontSize: 11 }}>{mineCount}</span>}
        </Pill>
        <div style={{ width: 1, height: 22, background: 'rgba(0,0,0,0.10)' }} />
        {['all', ...Object.keys(ROLES)].map(f => {
          const r = ROLES[f];
          const count = f === 'all' ? all.length : all.filter(t => t.roles?.slug === f || t.goals?.roles?.slug === f).length;
          return (
            <Pill key={f} active={filter === f} color={r?.color} bg={r?.light} border={r?.border} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : r.label}
              {count > 0 && <span style={{ marginLeft: 5, opacity: 0.65, fontSize: 11 }}>{count}</span>}
            </Pill>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {p1.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.red }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.red, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Do today
              </span>
              <span style={{ fontSize: 11, color: T.textMuted }}>{p1.length}</span>
            </div>
            <InlineTaskInput priority={1} onCreated={handleUpdate} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <AnimatePresence>
                {p1.map((t, i) => (
                  <motion.div key={t.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ delay: i * 0.02, ...springs.gentle }}>
                    <TaskRow task={t} onToggle={handleUpdate} showEta />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {p2.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.amber }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.amber, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                This week
              </span>
              <span style={{ fontSize: 11, color: T.textMuted }}>{p2.length}</span>
            </div>
            <InlineTaskInput priority={2} onCreated={handleUpdate} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <AnimatePresence>
                {p2.map((t, i) => (
                  <motion.div key={t.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ delay: i * 0.02, ...springs.gentle }}>
                    <TaskRow task={t} onToggle={handleUpdate} showEta />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Show input even when no tasks exist */}
        {p1.length === 0 && p2.length === 0 && (
          <div>
            <InlineTaskInput priority={1} onCreated={handleUpdate} />
          </div>
        )}

        {filtered.length === 0 && (
          <Glass style={{ padding: 48, textAlign: 'center' }} hover={false}>
            <CheckCircle2 size={32} color={T.green} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 15, color: T.textMid, fontWeight: 500 }}>All clear.</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginTop: 6 }}>
              {mineOnly ? 'Nothing is assigned to you here.' : 'No open tasks in this category.'}
            </div>
          </Glass>
        )}
      </div>
    </motion.div>
  );
};
